// src/components/settings/DepositSettings.js
//
// Org-level deposit policy. Writes the two org-doc fields the deposit
// feature reads but nothing previously set:
//   depositRequiredDefault — bool, top level. Whether a paid deposit GATES
//       estimate approval. Read by approveQuoteV2, QuoteFormModal, the
//       customer portal, and firestore.rules.
//   depositDefaults        — { enabled, type, value }. Pre-fills the deposit
//       controls on a brand-new estimate. Read by QuoteFormModal only.
// Both are org DEFAULTS: the per-estimate setting always overrides them, and
// existing estimates are never touched.
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { Wallet, Lock, Clock, AlertTriangle, CheckCircle } from 'lucide-react';
import {
    resolveDocTerminology,
    resolveDepositDefaults,
    DEPOSIT_TYPES,
} from '../../utils/quoteSchema';
// SEAM 1a VERIFIER: dsettings-orgpay-import-e5f6a7b8
// Client-side Stripe-onboarding gate — mirrors the server deposit gate so an
// org can't enable deposit defaults it could never collect against.
import { canOrgTakeOnlinePayments } from '../../utils/orgPayments'; // payhide-7e4c1b05 -- was isOrgStripeReady, which missed a restricted Stripe account

export default function DepositSettings({ currentUserProfile }) {
    const orgId = currentUserProfile?.organizationId;

    const [requiredDefault, setRequiredDefault] = useState(false);
    const [defaultEnabled,  setDefaultEnabled]  = useState(false);
    const [defaultType,     setDefaultType]     = useState(DEPOSIT_TYPES.PERCENTAGE);
    const [defaultValue,    setDefaultValue]    = useState('');
    const [currency,        setCurrency]        = useState('USD');
    // Aggregate/org-default surface — org term, NOT resolveDocTerminology(quote.docType).
    const [docWord,  setDocWord]  = useState('Quote');
    const [isSaving, setIsSaving] = useState(false);
    const [saved,    setSaved]    = useState(false);
    const [error,    setError]    = useState('');
    const [loading,  setLoading]  = useState(true);
    // SEAM 1a VERIFIER: dsettings-stripeready-state-f6a7b8c9
    const [stripeReady, setStripeReady] = useState(false);

    useEffect(() => {
        if (!orgId) return;
        return onSnapshot(doc(db, 'organizations', orgId), (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                const d    = resolveDepositDefaults(data.depositDefaults);
                setRequiredDefault(data.depositRequiredDefault === true);
                setDefaultEnabled(d.enabled);
                setDefaultType(d.type);
                setDefaultValue(d.value > 0 ? String(d.value) : '');
                setCurrency(data.currency || 'USD');
                setDocWord(resolveDocTerminology(data.quoteTerminologyDefault).word);
                setStripeReady(canOrgTakeOnlinePayments(data)); // dsettings-stripeready-state-f6a7b8c9 / payhide-7e4c1b05
            }
            setLoading(false);
        });
    }, [orgId]);

    const handleSave = async () => {
        setError('');
        // Same rules the per-estimate deposit card enforces (QuoteFormModal).
        const v = Number(defaultValue);
        if (defaultEnabled) {
            if (!Number.isFinite(v) || v <= 0) {
                setError('Enter a default deposit greater than zero, or turn off "Set a default deposit amount".');
                return;
            }
            if (defaultType === DEPOSIT_TYPES.PERCENTAGE && v > 100) {
                setError("A percentage deposit can't be more than 100%.");
                return;
            }
        }
        setIsSaving(true);
        try {
            // Dot paths so this write can never clobber a sibling org field.
            await updateDoc(doc(db, 'organizations', orgId), {
                depositRequiredDefault:    requiredDefault,
                'depositDefaults.enabled': defaultEnabled,
                'depositDefaults.type':    defaultType,
                'depositDefaults.value':   defaultEnabled ? v : 0,
            });
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            console.error('Error saving deposit settings:', err);
            setError('Failed to save. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) return <div className="p-6 text-sm text-gray-400">Loading…</div>;

    const word      = docWord.toLowerCase();
    const isPercent = defaultType === DEPOSIT_TYPES.PERCENTAGE;

    return (
        <div className="p-6 space-y-6 max-w-2xl">
            <div>
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <Wallet size={18} className="text-gray-500" /> Deposits
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                    Set how deposits work by default on new {word}s. You can still change the
                    deposit on any individual {word} — the {word}'s own setting always wins,
                    and existing {word}s are never changed by anything on this page.
                </p>
            </div>

            {/* SEAM 1d VERIFIER: dsettings-offline-note-e2c7a9f4
                Neutral offline-collection note. Shown only when the org has no
                Stripe. No setup link, no "connect" language — collecting every
                deposit offline is a valid permanent choice, not an unfinished
                setup. The toggles are no longer gated. */}
            {!stripeReady && (
                <div className="flex items-start gap-2 p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600">
                    <AlertTriangle size={16} className="shrink-0 mt-0.5 text-gray-400" />
                    <p>Your business isn't set up for online payments, so customers won't see a Pay button for these deposits. You'll collect each deposit directly — cheque, cash, e-transfer, or your own terminal — and record it on the {word} when it arrives.</p>
                </div>
            )}

            {/* Require deposits by default */}
            <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl">
                <div>
                    <p className="text-sm font-semibold text-gray-800">Require deposits by default</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                        The customer must pay before they can approve a new {word}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setRequiredDefault(p => !p)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 disabled:opacity-60 disabled:cursor-not-allowed ${requiredDefault ? 'bg-blue-600' : 'bg-gray-200'}`}
                >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${requiredDefault ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
            </div>

            {/* What "required" actually means */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-2">
                <p className="text-xs font-semibold text-blue-800">What this setting changes:</p>
                <div className="flex items-start gap-2">
                    <Lock size={12} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-700">
                        <strong>Required</strong> — the customer has to pay the deposit before
                        they can approve the {word}. The Approve button stays locked until the
                        payment lands, then the {word} approves.
                    </p>
                </div>
                <div className="flex items-start gap-2">
                    <Clock size={12} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-700">
                        <strong>Optional</strong> — the customer can approve the {word} right
                        away and pay the deposit afterwards. You still get the {word} approved
                        and the deposit request on record.
                    </p>
                </div>
                <div className="flex items-start gap-2">
                    <Wallet size={12} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-700">
                        Either way, a paid deposit is credited against the invoice when the
                        job is billed.
                    </p>
                </div>
            </div>

            {/* Default amount */}
            <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl">
                <div>
                    <p className="text-sm font-semibold text-gray-800">Set a default deposit amount</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                        New {word}s open with this deposit already filled in
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setDefaultEnabled(p => !p)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 disabled:opacity-60 disabled:cursor-not-allowed ${defaultEnabled ? 'bg-blue-600' : 'bg-gray-200'}`}
                >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${defaultEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
            </div>

            {defaultEnabled && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
                    <div className="flex flex-wrap items-end gap-3">
                        <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Type</label>
                            <select
                                value={defaultType}
                                onChange={(e) => setDefaultType(e.target.value)}
                                className="p-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                <option value={DEPOSIT_TYPES.PERCENTAGE}>Percentage</option>
                                <option value={DEPOSIT_TYPES.FIXED}>Fixed amount</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">
                                {isPercent ? 'Percent of total' : `Amount (${currency})`}
                            </label>
                            <div className="relative">
                                {!isPercent && (
                                    <span className="absolute left-2 top-2.5 text-gray-400 text-xs">$</span>
                                )}
                                <input
                                    type="number"
                                    value={defaultValue}
                                    onChange={(e) => setDefaultValue(e.target.value)}
                                    min="0"
                                    max={isPercent ? '100' : '999999.99'}
                                    step="0.01"
                                    placeholder={isPercent ? '25' : '500.00'}
                                    className={`w-32 p-2 ${isPercent ? 'pr-7' : 'pl-5'} border border-gray-300 rounded-lg text-sm text-right font-semibold focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-60 disabled:cursor-not-allowed`}
                                />
                                {isPercent && (
                                    <span className="absolute right-2 top-2.5 text-gray-400 text-xs">%</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <p className="text-xs text-gray-400">
                        {isPercent
                            ? `Worked out from the ${word} total at the moment the customer approves, so it stays correct if the ${word} changes.`
                            : `The same amount on every new ${word}, no matter the total. Capped at the ${word} total.`}
                    </p>
                </div>
            )}

            {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <AlertTriangle size={14} /> {error}
                </div>
            )}

            <div className="flex items-center gap-3">
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                    {isSaving ? 'Saving…' : 'Save'}
                </button>
                {saved && (
                    <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
                        <CheckCircle size={14} /> Saved
                    </span>
                )}
            </div>
        </div>
    );
}
